import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"

// bulk-select
//
// Row + section checkboxes drive the floating bulk action bar (V2::BulkActionBarComponent). Put this
// controller on an element the list's turbo-stream re-render does NOT replace, so the bar survives
// while the re-rendered row checkboxes come back unchecked, clearing the selection after an apply.
//
// The server answers with turbo-stream, and that response decides what is redrawn. After a
// successful apply this dispatches `bulk-select:applied` for anything else the page needs to refresh.
//
// Selection is always computed from the SEARCH-VISIBLE row checkboxes (list-filter hides non-matching
// rows via inline display:none), so a section select-all and the count never touch filtered-out rows.
// A section is the nearest [data-bulk-section] around a section checkbox.
//
// A row checkbox's data-bulk-weight is how many items it stands for (default 1), e.g. a group row
// on the Issues list; the bar counts the sum. After an apply the bar repeats that count, unless
// data-bulk-select-announce-count-value is false.
//
// An operation button with a data-bulk-select-confirm-param opens the bar's confirm dialog with that
// sentence, and sends only from the dialog's button.
export default class extends Controller {
  static targets = ["rowCheckbox", "sectionCheckbox", "bar", "count", "more",
    "confirmDialog", "confirmTitle", "confirmBody", "confirmButton"]
  static values = {
    url: String,
    method: { type: String, default: "post" },
    // The body key the selected checkbox values are sent under, e.g. "conversion_action_ids".
    param: String,
    noun: { type: String, default: "item" },
    // Off where the server's message says how many changed: the ticked count would contradict it whenever some
    // ticked items were already in the chosen state.
    announceCount: { type: Boolean, default: true },
  }

  // A page that leaves either out would send its ids nowhere, and the server would just see none.
  connect() {
    if (!this.urlValue || !this.paramValue) {
      throw new Error("bulk-select needs data-bulk-select-url-value and data-bulk-select-param-value")
    }
  }

  // Re-render of the list swaps every row checkbox → recompute (selection resets to empty).
  rowCheckboxTargetConnected() { this.refresh() }
  rowCheckboxTargetDisconnected() { this.refresh() }

  toggleRow() { this.refresh() }

  // Header select-all: mirror its state onto every visible row checkbox in the same section.
  toggleSection(event) {
    const section = event.target.closest("[data-bulk-section]")
    this.visibleRowCheckboxesIn(section).forEach((cb) => { cb.checked = event.target.checked })
    this.refresh()
  }

  clear() {
    this.rowCheckboxTargets.forEach((cb) => { cb.checked = false })
    this.refresh()
  }

  apply(event) {
    if (this.selectedIds().length === 0) return
    this.closeMore()

    const operation = event.params.operation
    const label = event.currentTarget.textContent.trim()
    if (!event.params.confirm) return this.send(operation, label)

    this.pending = { operation, label }
    this.confirmTitleTarget.textContent = `${label} ${this.countLabel(this.selectedCount())}?`
    this.confirmBodyTarget.textContent = event.params.confirm
    this.confirmButtonTarget.textContent = label
    this.confirmDialogTarget.showModal()
  }

  confirm() {
    this.confirmDialogTarget.close()
    if (this.pending) this.send(this.pending.operation, this.pending.label)
    this.pending = null
  }

  async send(operation, label) {
    const ids = this.selectedIds()
    if (ids.length === 0) return
    const count = this.selectedCount()

    const request = new FetchRequest(this.methodValue, this.urlValue, {
      body: JSON.stringify({ operation, [this.paramValue]: ids }),
      contentType: "application/json",
      responseKind: "turbo-stream",
    })
    let response
    try {
      response = await request.perform()
    } catch {
      // request.js does a bare `await window.fetch`, so a network-level failure THROWS rather than
      // returning a non-ok response; without this the bar would keep its pre-click text.
      this.announce(`Couldn't apply "${label}" — nothing changed. Try again.`, true)
      return
    }

    // Without this a rejected operation would look like success: the bar still holding the selection.
    if (!response.ok) {
      this.announce(`Couldn't apply "${label}" — nothing changed. Try again.`, true)
      return
    }

    // Rows can move on re-render, and on a long list that happens off-screen, so say what changed here
    // rather than leaving the reader to hunt for it.
    this.announce(this.announceCountValue ? `${label} · ${this.countLabel(count)}` : `${label} applied`)
    this.dispatch("applied", { detail: { operation, ids } })
  }

  // Hold a message in the bar for a moment. refresh() runs on every checkbox swap (the re-render
  // fires one per row), so it has to leave the message alone or the confirmation never survives the
  // render that prompted it.
  announce(message, isError = false) {
    if (!this.hasCountTarget) return

    clearTimeout(this.announceTimer)
    this.announcing = true
    this.countTarget.textContent = message
    this.countTarget.classList.toggle("text-sg-error-light", isError)
    if (this.hasBarTarget) this.barTarget.classList.remove("hidden")

    this.announceTimer = setTimeout(() => {
      this.announcing = false
      this.countTarget.classList.remove("text-sg-error-light")
      this.refresh()
    }, 4000)
  }

  refresh() {
    this.syncSectionCheckboxes()
    if (this.announcing) return

    const count = this.selectedCount()
    if (this.hasCountTarget) this.countTarget.textContent = `Bulk updating ${this.countLabel(count)}`
    if (this.hasBarTarget) this.barTarget.classList.toggle("hidden", count === 0)
    if (count === 0) this.closeMore()
  }

  disconnect() {
    clearTimeout(this.announceTimer)
  }

  countLabel(count) {
    return `${count} ${count === 1 ? this.nounValue : `${this.nounValue}s`}`
  }

  syncSectionCheckboxes() {
    this.sectionCheckboxTargets.forEach((box) => {
      const boxes = this.visibleRowCheckboxesIn(box.closest("[data-bulk-section]"))
      const checked = boxes.filter((cb) => cb.checked).length
      box.checked = boxes.length > 0 && checked === boxes.length
      box.indeterminate = checked > 0 && checked < boxes.length
    })
  }

  closeMore() {
    if (this.hasMoreTarget) this.moreTarget.removeAttribute("open")
  }

  // A <details> menu stays open until its own summary is clicked again, which traps the reader.
  // Matches how every other dropdown in the app behaves (see nav_profile_dropdown_controller).
  closeMoreOnOutsideClick(event) {
    if (!this.hasMoreTarget || !this.moreTarget.open) return
    if (this.moreTarget.contains(event.target)) return

    this.closeMore()
  }

  closeMoreOnEscape(event) {
    if (event.key === "Escape") this.closeMore()
  }

  selectedIds() {
    return this.selectedRowCheckboxes().map((cb) => cb.value)
  }

  selectedCount() {
    return this.selectedRowCheckboxes().reduce((sum, cb) => sum + Number(cb.dataset.bulkWeight || 1), 0)
  }

  selectedRowCheckboxes() {
    return this.visibleRowCheckboxes().filter((cb) => cb.checked)
  }

  visibleRowCheckboxes() {
    return this.rowCheckboxTargets.filter((cb) => this.isSearchVisible(cb))
  }

  visibleRowCheckboxesIn(section) {
    if (!section) return []
    return this.visibleRowCheckboxes().filter((cb) => section.contains(cb))
  }

  isSearchVisible(cb) {
    const item = cb.closest("[data-list-filter-target='item']")
    return !item || item.style.display !== "none"
  }
}
